import React from "react";
import { Link } from "react-router-dom";
import { Megaphone, Code, Sparkle, ArrowRight, Check } from "@phosphor-icons/react";
import { useI18n } from "../i18n/I18nContext";
import { useSiteContent } from "../i18n/SiteContent";

const GROUP_ICONS = [Megaphone, Code, Sparkle];

// Map service group titles to slugs (aligns with page-content service.<slug>.detail)
const SERVICE_SLUGS = {
  "digital marketing":      "digital-marketing",
  "it consulting":          "it-consulting",
  "it services":            "it-consulting",
  "ai & automation":        "ai-automation",
  "ai and automation":      "ai-automation",
  "ai & software automation": "ai-automation",
};

const slugFor = (title = "") => {
  const key = title.toLowerCase().trim();
  return SERVICE_SLUGS[key] || key.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
};

export default function Services() {
  const { t, isRTL } = useI18n();
  const { pg, pgList } = useSiteContent();
  const eyebrow = pg("services.eyebrow", t.services.eyebrow);
  const heading = pg("services.heading", t.services.heading);
  const sub = pg("services.sub", t.services.sub);
  const learnMore = pg("services.cta_learn_more", t.services.learnMore);
  const groups = pgList("services.groups", t.servicesMega.groups);

  return (
    <section id="services" data-testid="services-section" className="py-8 md:py-14 reveal" style={{ background: "#F5F7FA" }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-4xl">
          <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-[54px] leading-[1.05] anim anim-up" style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
            {heading}
          </h2>
          <p className="mt-6 text-[#6B7A8D] text-lg md:text-xl font-light leading-relaxed max-w-2xl">
            {sub}
          </p>
        </div>

        <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 stagger anim">
          {groups.map((g, gi) => {
            const Icon = GROUP_ICONS[gi];
            return (
              <div
                key={gi}
                data-testid={`service-group-${gi}`}
                className="anim anim-up card-lift group relative bg-white rounded-2xl p-8 border border-slate-200 overflow-hidden"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                  style={{ background: "#1B6FE0" }}
                />
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-6 transition"
                  style={{ background: "rgba(15,42,110,0.05)", color: "#0F2A6E" }}
                >
                  <Icon size={20} weight="duotone" />
                </div>
                <h3 className="font-heading font-semibold text-[20px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.01em" }}>
                  {g.title}
                </h3>
                <p className="mt-2 text-[13.5px] text-[#6B7A8D] leading-relaxed">{g.blurb}</p>

                <ul className="mt-6 space-y-2.5">
                  {(g.items || []).map((it, i) => {
                    const isObj = typeof it === "object" && it !== null;
                    const label = isObj ? it.title : it;
                    return (
                      <li
                        key={i}
                        data-testid={`service-item-${gi}-${i}`}
                        className="flex items-start gap-2.5 text-[13.5px] text-[#1C2B3A] leading-relaxed"
                      >
                        <Check size={14} weight="bold" className="mt-1 shrink-0" style={{ color: "#1B6FE0" }} />
                        <span>{label}</span>
                      </li>
                    );
                  })}
                </ul>

                <button
                  data-testid={`service-cta-${gi}`}
                  onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-7 inline-flex items-center gap-1.5 text-[13px] font-medium transition group-hover:gap-3"
                  style={{ color: "#1B6FE0" }}
                >
                  {learnMore}
                  <ArrowRight size={14} weight="bold" className={isRTL ? "rtl-flip" : ""} />
                </button>

                <Link
                  to={`/services/${slugFor(g.title)}`}
                  data-testid={`service-detail-link-${gi}`}
                  className="absolute inset-0"
                  aria-label={`Open ${g.title} details`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
